import { computed, onUnmounted, ref, watch } from 'vue'

import { useUserInfoLoaded } from '@/composables/use-user-state'
import { useAppStore } from '@/store/app-store'
import { useGoogleAuthStore } from '@/store/google-auth-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { SIGN_IN_MODAL_WEIGHT, useNotificationsQueue } from '@/store/notification-que/notifications-queue'
import { useUserStore } from '@/store/user-store/user-store'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

const AUTO_SIGN_IN_DELAY_MS = 2000
const AUTO_SIGN_IN_SOURCE = 'profile-page-auto-sign-in'
const AUTO_SIGN_IN_NOTIFICATION = 'sign-in-modal'
const ONE_TAP_NOTIFICATION = 'auth-notification'

export function useAutoSignInPrompt() {
    const appStore = useAppStore()
    const userStore = useUserStore()
    const layoutStore = useLayoutStore()
    const googleAuthStore = useGoogleAuthStore()
    const loggerStore = useLoggerStore()
    const notificationsQueue = useNotificationsQueue()

    const isMounted = useIsMounted()
    const userInfoLoaded = useUserInfoLoaded()
    const isSuppressed = ref(false)

    const shouldPromptSignIn = computed(
        () =>
            isMounted.value &&
            !isSuppressed.value &&
            appStore.platform !== 'desktop' &&
            userInfoLoaded.value &&
            !userStore.isAuthorized,
    )

    let autoOpenTimeoutId: ReturnType<typeof setTimeout> | null = null
    const isDelayElapsed = ref(false)
    // the auto modal already had its turn; a queue driven preemption in onClose takes that back
    let hasShownModal = false

    /*
      One tap outranks us: while its item is around, request() either drops ours outright or dispatch() clears it
      with forceCloseAndClearNotActive(). A queued item is invisible in activeName until init(), so track presence
      in the queue itself - it covers both the queued and the active state.
    */
    const isOneTapPending = computed(() =>
        notificationsQueue.notificationsQueue.some((item) => item.name === ONE_TAP_NOTIFICATION),
    )

    // checked right before requesting and again before opening, never in the eligibility watcher:
    // a dismissed moment arriving after the modal is up must not close it
    function canOpenSignInModal() {
        return shouldPromptSignIn.value && !googleAuthStore.promptDismissedThisVisit
    }

    function requestSignInModal() {
        if (hasShownModal || !isDelayElapsed.value || isOneTapPending.value || !canOpenSignInModal()) {
            return
        }

        notificationsQueue.request({
            name: AUTO_SIGN_IN_NOTIFICATION,
            weight: SIGN_IN_MODAL_WEIGHT,
            onOpen: () => {
                // the request can sit in a non-initialized queue for a while, recheck before opening
                if (!canOpenSignInModal()) {
                    notificationsQueue.remove(AUTO_SIGN_IN_NOTIFICATION)
                    return
                }

                hasShownModal = true
                layoutStore.openSignInModal(AUTO_SIGN_IN_SOURCE)
                loggerStore.logEvent({
                    event: 'custom_event',
                    eventName: 'auth_popup_auto_opened',
                    dimension1: AUTO_SIGN_IN_SOURCE,
                })
            },
            onClose: () => {
                /*
                  A modal still open here means the queue is taking the floor from us, not the user closing it:
                  the user's own close flips signInModalOpened first and only then removes the item.
                  A preempted modal never had its full turn, so it may come back once the floor is free again.
                */
                if (layoutStore.signInModalOpened) {
                    hasShownModal = false
                }

                layoutStore.closeSignInModal()
            },
        })
    }

    function cancel() {
        if (autoOpenTimeoutId) {
            clearTimeout(autoOpenTimeoutId)
            autoOpenTimeoutId = null
        }

        isDelayElapsed.value = false
        notificationsQueue.remove(AUTO_SIGN_IN_NOTIFICATION)
    }

    watch(
        shouldPromptSignIn,
        (value) => {
            if (!value) {
                cancel()
                return
            }

            if (autoOpenTimeoutId) {
                return
            }

            autoOpenTimeoutId = setTimeout(() => {
                autoOpenTimeoutId = null
                isDelayElapsed.value = true
                requestSignInModal()
            }, AUTO_SIGN_IN_DELAY_MS)
        },
        { immediate: true },
    )

    // one tap leaving the queue is our cue to try again: the first attempt was either skipped or cleared by it
    watch(isOneTapPending, requestSignInModal)

    watch(
        () => [layoutStore.signInModalOpened, layoutStore.signInModalOpenedSource] as const,
        ([opened, source]) => {
            if (opened && source !== AUTO_SIGN_IN_SOURCE) {
                // the user opened the modal themselves, the page prompt stands down for this mount
                isSuppressed.value = true
                cancel()
                return
            }

            if (!opened) {
                notificationsQueue.remove(AUTO_SIGN_IN_NOTIFICATION)
            }
        },
    )

    onUnmounted(cancel)
}
